import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Grid, Card, CardContent, Typography, LinearProgress,
  Button, Chip, Avatar,
} from '@mui/material';
import {
  Assignment as TaskIcon,
  TrendingUp as TrendingUpIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Warning as WarningIcon,
  Dashboard as BoardIcon,
  TableRows as ListIcon,
} from '@mui/icons-material';
import { useHistory } from 'react-router-dom';
import apiService from '../../services/apiServices';
import Toast from '../actions/Toasts';

const STATUS_LABELS = { pending: "\u00ebn T'u B\u00ebrë", in_progress: 'N\u00eb Proces', on_hold: 'E Pezulluar', completed: 'E P\u00ebrfunduar', cancelled: 'E Anuluar' };
const STATUS_COLORS = { pending: 'warning', in_progress: 'info', on_hold: 'warning', completed: 'success', cancelled: 'default' };

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString('sq-AL', { day: '2-digit', month: 'short' }) : '\u2014';

function StatCard({ title, value, icon, color, sub }) {
  return (
    <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, height: '100%' }}>
      <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.72rem', display: 'block', mb: 0.25 }}>
              {title}
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 700, color, fontSize: { xs: '1.5rem', md: '2rem' }, lineHeight: 1 }}>
              {value}
            </Typography>
            {sub && (
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem', mt: 0.25, display: 'block' }}>
                {sub}
              </Typography>
            )}
          </Box>
          <Box sx={{ width: 40, height: 40, borderRadius: 1.5, bgcolor: `${color}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            {React.cloneElement(icon, { sx: { fontSize: 22, color } })}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}

export default function TasksDashboard() {
  const history = useHistory();
  const [tasks, setTasks]     = useState([]);
  const [loading, setLoading] = useState(false);
  const [toast, setToast]     = useState({ open: false, message: '', variant: 'info' });

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const res = await apiService().get('tasks');
        setTasks(res.data || []);
      } catch {
        setToast({ open: true, message: 'Gabim n\u00eb ngarkimin e detyrave', variant: 'error' });
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const stats = useMemo(() => {
    const now = new Date();
    return {
      total:       tasks.length,
      pending:     tasks.filter((t) => t.status === 'pending').length,
      in_progress: tasks.filter((t) => t.status === 'in_progress').length,
      completed:   tasks.filter((t) => t.status === 'completed').length,
      overdue:     tasks.filter((t) =>
        t.status !== 'completed' && t.status !== 'cancelled' && t.due_date && new Date(t.due_date) < now
      ).length,
    };
  }, [tasks]);

  const completionRate = stats.total ? Math.round((stats.completed / stats.total) * 100) : 0;
  const recent = tasks.slice().sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0)).slice(0, 8);

  return (
    <Box>
      <Toast {...toast} />

      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 1 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.25 }}>Paneli i Detyrave</Typography>
          <Typography variant="body2" color="text.secondary">Pasqyr\u00eb e p\u00ebrgjithshme e detyrave dhe progresit</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined" size="small" startIcon={<ListIcon />}
            onClick={() => history.push('/tasks')}
            sx={{ textTransform: 'none', borderRadius: 2, fontWeight: 600 }}
          >
            Lista
          </Button>
          <Button
            variant="contained" size="small" startIcon={<BoardIcon />}
            onClick={() => history.push('/tasks/board')}
            sx={{ textTransform: 'none', borderRadius: 2, fontWeight: 600 }}
          >
            Bordi Kanban
          </Button>
        </Box>
      </Box>

      {loading && <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />}

      {/* Stat cards */}
      <Grid container spacing={1.5} sx={{ mb: 3 }}>
        {[
          { title: 'Totali',      value: stats.total,       icon: <TaskIcon />,        color: '#3E8FA3' },
          { title: 'N\u00eb Pritje',   value: stats.pending,     icon: <ScheduleIcon />,    color: '#f59e0b' },
          { title: 'N\u00eb Progres',  value: stats.in_progress, icon: <TrendingUpIcon />,  color: '#6366f1' },
          { title: 'Kompletuara', value: stats.completed,   icon: <CheckCircleIcon />, color: '#22c55e', sub: `${completionRate}% e totalit` },
          { title: 'T\u00eb Vonuara',  value: stats.overdue,     icon: <WarningIcon />,     color: '#ef4444' },
        ].map((s) => (
          <Grid key={s.title} item xs={6} sm={4} md={12 / 5}>
            <StatCard {...s} />
          </Grid>
        ))}
      </Grid>

      {/* Progress bar */}
      <Card elevation={0} sx={{ mb: 3, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
        <CardContent sx={{ p: { xs: 2, sm: 2.5 }, '&:last-child': { pb: { xs: 2, sm: 2.5 } } }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>Progresi i P\u00ebrgjithsh\u00ebm</Typography>
            <Typography variant="body2" sx={{ fontWeight: 700, color: '#22c55e' }}>{completionRate}%</Typography>
          </Box>
          <LinearProgress
            variant="determinate" value={completionRate}
            sx={{ height: 8, borderRadius: 4, bgcolor: '#f0fdf4', '& .MuiLinearProgress-bar': { borderRadius: 4, bgcolor: '#22c55e' } }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
            <Typography variant="caption" color="text.secondary">{stats.completed} t\u00eb kompletuara</Typography>
            <Typography variant="caption" color="text.secondary">{stats.total - stats.completed} t\u00eb mbetura</Typography>
          </Box>
        </CardContent>
      </Card>

      {/* Recent tasks */}
      <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
        <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
          <Box sx={{ px: 2.5, py: 1.75, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="body1" sx={{ fontWeight: 700 }}>Detyrat e fundit</Typography>
            <Button size="small" onClick={() => history.push('/tasks/board')}
              sx={{ textTransform: 'none', fontSize: '0.78rem', fontWeight: 600 }}>
              Shiko bordin \u2192
            </Button>
          </Box>
          <Box sx={{ borderTop: '1px solid', borderColor: 'divider' }}>
            {recent.length === 0 && !loading && (
              <Box sx={{ py: 4, textAlign: 'center' }}>
                <TaskIcon sx={{ fontSize: 36, color: 'text.disabled', mb: 1 }} />
                <Typography variant="body2" color="text.secondary">Nuk ka detyra</Typography>
              </Box>
            )}
            {recent.map((task, i) => (
              <Box
                key={task.id}
                onClick={() => history.push('/tasks/board')}
                sx={{
                  px: 2.5, py: 1.25, display: 'flex', alignItems: 'center', gap: 1.5, cursor: 'pointer',
                  borderBottom: i < recent.length - 1 ? '1px solid' : 'none',
                  borderColor: 'divider',
                  '&:hover': { bgcolor: 'action.hover' },
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.83rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {task.title}
                  </Typography>
                  {task.assigned_to_name && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                      <Avatar sx={{ width: 14, height: 14, fontSize: 8, bgcolor: 'primary.main' }}>
                        {task.assigned_to_name[0].toUpperCase()}
                      </Avatar>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem' }}>
                        {task.assigned_to_name}
                      </Typography>
                    </Box>
                  )}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                  <Chip label={STATUS_LABELS[task.status] || task.status} color={STATUS_COLORS[task.status] || 'default'} size="small" />
                  {task.due_date && (
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.72rem' }}>
                      {fmtDate(task.due_date)}
                    </Typography>
                  )}
                </Box>
              </Box>
            ))}
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}
